<script setup lang="ts">
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/cms/components/ui/dialog/index.ts";
import { Button } from "@/cms/components/ui/button/index.ts";
import { VisuallyHidden } from "radix-vue";
import type { DialogProps } from "@/cms/components/Dialog/Dialog.types.ts";
import DialogTitle from "@/cms/components/ui/dialog/DialogTitle.vue";
import DialogDescription from "@/cms/components/ui/dialog/DialogDescription.vue";
import { computed } from "vue";
import { cn } from "@/cms/utils.ts";

const props = withDefaults(defineProps<DialogProps>(), {
  hasCloseButton: false,
  hasXButton: true,
  size: "default",
  variant: "default",
  preventCloseOnOutsideClick: false,
  preventCloseOnEscape: false,
  disableBackdrop: false,
  modal: true,
});

const model = defineModel({ type: Boolean, required: true });

const dialogContentClasses = computed(() => {
  const classes = ["sm:max-w-md", "max-h-full", "p-3", "sm:p-6"];

  if (props.size === "large") {
    classes.push("sm:h-[80%] min-w-[80%] max-h-[calc(100dvh)]");
  }

  if (props.size === "medium") {
    classes.push("sm:h-[70%] min-w-[40%] max-h-[calc(100dvh)]");
  }

  if (props.variant === "fullbleed") {
    classes.push("border-none overflow-hidden p-0 sm:p-0");
  }

  if (props.class) {
    classes.push(props.class);
  }

  return classes;
});

const hideTitle = computed(() => props.variant === "fullbleed");
const hideDescription = computed(
  () => props.hideDescription || props.variant === "fullbleed"
);
const hideFooter = computed(() => props.variant === "fullbleed");

/*
Prevents the dialog from closing when image editor is used
 */
function onInteractOutside(event: Event) {
  if (props.preventCloseOnOutsideClick) {
    event.preventDefault();
  }

  const pinturaOpen = document.querySelector(".pintura-editor");

  if (pinturaOpen) {
    event.preventDefault();
  }
}

/*
Prevents the dialog from closing when the image editor is used
 */
function onEscapeKeyDown(event: KeyboardEvent) {
  if (props.preventCloseOnEscape) {
    event.preventDefault();
  }

  const pinturaOpen = document.querySelector(".pintura-editor");

  if (pinturaOpen) {
    event.preventDefault();
  }
}

const headerClasses = computed(() => {
  if (props.variant === "fullbleed") {
    return "sr-only";
  }
  return "";
});
</script>

<template>
  <!-- @vue-ignore -->
  <Dialog v-model:open="model" :modal>
    <DialogContent
      :class="dialogContentClasses"
      :has-x-button
      :disable-backdrop
      class="focus-visible:outline-white grid-rows-[auto_1fr_auto]"
      data-testid="cms-dialog-content"
      @interact-outside="onInteractOutside"
      @escape-key-down="onEscapeKeyDown"
    >
      <DialogHeader :class="headerClasses">
        <VisuallyHidden v-if="hideTitle">
          <DialogTitle>
            {{ title }}
          </DialogTitle>
        </VisuallyHidden>

        <DialogTitle v-else> {{ title }} </DialogTitle>

        <VisuallyHidden v-if="hideDescription">
          <DialogDescription>
            {{ description }}
          </DialogDescription>
        </VisuallyHidden>
        <DialogDescription v-else>
          {{ description }}
        </DialogDescription>
      </DialogHeader>

      <slot />

      <DialogFooter v-if="!hideFooter" :class="cn('sm:justify-end')">
        <slot name="buttons" />

        <DialogClose v-if="hasCloseButton" as-child>
          <Button
            type="button"
            data-testid="cms-dialog-close-button"
            :data-dd="`${title} Dialog Close Button`"
          >
            Close
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
